import {
  Button,
  DefinitionTooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import type { PromptGroup, PromptVersion } from './types';
import { dash, num, date } from './format';
import styles from './page.module.scss';

interface Props {
  group: PromptGroup;
  onViewVersion: (version: PromptVersion) => void;
}

export function VersionLogSubrow({ group, onViewVersion }: Props) {
  const usageLabel =
    group.kind === 'agent'
      ? 'Total Records Evaluated'
      : 'Total Documents Processed';
  const usageColumnLabel =
    group.kind === 'agent' ? 'Records Evaluated' : 'Documents Processed';

  return (
    <div className={styles.subrow}>
      <div className={styles.keyDetailsBar}>
        <div>
          <span className={styles.keyLabel}>
            <DefinitionTooltip
              definition="Estimated from the stored template text (~4 chars/token). Excludes sections injected at request time."
              openOnHover
            >
              Est. Tokens
            </DefinitionTooltip>
          </span>
          <span data-testid="est-tokens-value">
            ~{num(group.estTokens)} tokens
          </span>
        </div>
        <div>
          <span className={styles.keyLabel}>{usageLabel}</span>
          <span data-testid="usage-total-value">
            {num(group.totalDocumentsProcessed)}
          </span>
        </div>
        <div>
          <span className={styles.keyLabel}>Last Updated</span>
          <span data-testid="last-updated-value">{date(group.updatedAt)}</span>
        </div>
      </div>

      <h3 className={styles.versionLogLabel}>Version Log</h3>

      <Table size="md">
        <TableHead>
          <TableRow>
            <TableHeader>Version</TableHeader>
            <TableHeader>Published Date</TableHeader>
            <TableHeader>Published By</TableHeader>
            <TableHeader>Change Note</TableHeader>
            <TableHeader>{usageColumnLabel}</TableHeader>
            <TableHeader>First Used</TableHeader>
            <TableHeader>Last Used</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {group.versions.map((version) => (
            <TableRow key={String(version.id)}>
              <TableCell>
                <div className={styles.versionCell}>
                  <span>{version.version}</span>
                  {version.active && (
                    <Tag type="green" size="sm">
                      Active
                    </Tag>
                  )}
                </div>
              </TableCell>
              <TableCell>{date(version.publishedDate)}</TableCell>
              <TableCell>{version.publishedBy}</TableCell>
              <TableCell>{dash(version.changeNote)}</TableCell>
              <TableCell>{num(version.documentsProcessed)}</TableCell>
              <TableCell>{date(version.firstUsed)}</TableCell>
              <TableCell>{date(version.lastUsed)}</TableCell>
              <TableCell>
                <Button
                  kind="ghost"
                  size="sm"
                  onClick={() => onViewVersion(version)}
                >
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
